import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { BiblioRecordList, BiblioRecord } from '../models/biblio-record';
import { BibFile } from '../models/bib-file';
import { BibListFilters } from '../models/bib-list-filters';

/* Http parameters type for bibliography */
export type BiblioHttpParamsTypes = 'biblioRecList' | 'biblioRec';

@Injectable()
export class BibliographyService {
    private httpHeaders: HttpHeaders;
    private httpParams: Map<BiblioHttpParamsTypes, HttpParams>;
    private bibListFilters$: BehaviorSubject<BibListFilters>;
    private biblioRecords$: BehaviorSubject<BiblioRecordList>;
    private biblioRecord$: BehaviorSubject<BiblioRecord>;

    constructor(private http: HttpClient) {
        this.init();
    }

    init(): void {
        /* Common headers */
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        /* Bilbiographic record list http params */
        this.httpParams = new Map<BiblioHttpParamsTypes, HttpParams>();
        this.httpParams.set('biblioRecList', new HttpParams()
            .set('fields', 'pkid,fkidstatus,quotation_label,title,data_journal_title,fkidbiblio_types,reference'
                + ',data_volume,data_volume_title,data_num_volumes,data_conference,data_url,data_consultation_date'
                + ',data_dissertation_date,data_university,data_year,data_fascicle_number,data_pages'
                + ',notes_text,data_edition_place,data_edition_date,data_publisher,data_book_series'
                + ',data_event,fkidbiblio_comm_types,version,pub_last_date')
            .set('expand', 'fkidbiblio_types,fkidbiblio_comm_types,__linkedFiles,__linkedBiblio,__linkedMs,__resps')
            .set('sort', 'reference_sort')
        );

        /* Bibliographic record http params */
        this.httpParams.set('biblioRec', new HttpParams()
            .set('expand', 'fkidbiblio_types,fkidbiblio_comm_types,__linkedBiblio,__resps')
        );

        /* Observables on bibliographic data */
        this.bibListFilters$ = new BehaviorSubject<BibListFilters>(new BibListFilters());
        this.biblioRecords$ = new BehaviorSubject<BiblioRecordList>([]);
        this.biblioRecord$ = new BehaviorSubject<BiblioRecord>(<BiblioRecord>{});
    }

    /* Parse filters and update http params */
    private updateFilterParams(bibListFilters: BibListFilters): void {
        const filtersString: string = bibListFilters.stringifyAllFilters();
        this.httpParams.set('biblioRecList', this.httpParams.get('biblioRecList').set('filter', filtersString));
    }

    /* Bibliographic record list API call */
    private loadBiblioRecords(): void {
        this.http.get<BiblioRecordList>(
            environment.endpoints.de + '/biblio',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('biblioRecList')
            })
            .subscribe(
                response => this.biblioRecords$.next(response),
                error => this.biblioRecords$.error(error)
            );
    }

    /* Single bibliographic record API call */
    private loadBiblioRecord(bibId: number): void {
        this.http.get<BiblioRecord>(
            environment.endpoints.de + '/biblio/' + bibId,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('biblioRec')
            })
            .subscribe(
                response => this.biblioRecord$.next(response),
                error => this.biblioRecord$.error(error)
            );
    }

    /* Update filters and reload bibliography by default */
    updateFilters(bibListFilters: BibListFilters, reloadBiblio: boolean = true): void {
        this.bibListFilters$.next(bibListFilters);
        this.updateFilterParams(bibListFilters);
        if (reloadBiblio === true) {
            this.loadBiblioRecords();
        }
    }

    /* Reset filters and reload bibliography list by default */
    resetFilters(reloadBiblio: boolean = true): void {
        this.updateFilters(new BibListFilters(), reloadBiblio);
    }

    /* Get bibliography filters */
    getBibListFilters(): Observable<BibListFilters> {
        return this.bibListFilters$.asObservable();
    }

    /* Get bibliographic record list */
    getBiblioRecords(): Observable<BiblioRecordList> {
        this.loadBiblioRecords();
        return this.biblioRecords$.asObservable();
    }

    /* Get single bibliographic record main data, if no valid record identifier is provided return last loaded data */
    getBiblioRecord(bibId?: number): Observable<BiblioRecord> {
        if (bibId) {
            this.loadBiblioRecord(bibId);
        }
        return this.biblioRecord$.asObservable();
    }

    /* Get a bibliographic uploaded file API call */
    getUploadedFile(bibFile: BibFile): Observable<string> {
        return this.http.get(
            environment.endpoints.de + '/biblio/' + bibFile.fkidbibliography + '/uploads/' + bibFile.pkid + '/file/' + bibFile.file_id,
            {
                headers: this.httpHeaders,
                responseType: 'blob'
            })
            .map(blob => {
                return URL.createObjectURL(blob);
            });
    }

}



// WEBPACK FOOTER //
// ./src/app/services/bibliography.service.ts